import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { ArrowLeft, CreditCard, Receipt, Sparkles, ChevronRight, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LearnerMoney — Phase 1 (Feb 6, 2026): the new "Money" tab.
 *
 * Phase 1 scope is STRUCTURAL ONLY: this page is a thin shell that
 * surfaces the learner's existing payments / subscription / upgrade
 * affordances in one focused place. No content has been moved out of
 * other pages yet — that lands in Phase 2.
 */
export default function LearnerMoney() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/lesson-payments/my-bookings/all`, { withCredentials: true });
        if (alive) setRecent(r.data?.bookings || r.data || []);
      } catch {
        if (alive) setRecent([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const isPremium = (user?.plan || "").toLowerCase() === "premium";
  const planLabel = isPremium ? "Premium" : (user?.plan ? user.plan[0].toUpperCase() + user.plan.slice(1) : "Free");

  // Surface only paid + upcoming rows so the page reads as "money".
  const paid = (recent || []).filter((b) => b?.payment_status === "paid").slice(0, 5);
  const upcoming = (recent || []).filter((b) => b?.payment_status === "unpaid" || b?.lifecycle_status === "pending").slice(0, 5);

  return (
    <div
      data-testid="learner-money-page"
      className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-24"
    >
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button
            data-testid="money-back-btn"
            onClick={() => nav("/learn")}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="font-extrabold text-slate-900 dark:text-white text-base">Money</p>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 pt-4 space-y-4">
        {/* Subscription status */}
        <Card testid="money-subscription">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${isPremium ? "bg-amber-100 dark:bg-amber-900/30 text-amber-600" : "bg-slate-100 dark:bg-slate-800 text-slate-500"} flex items-center justify-center`}>
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Current plan</p>
              <p data-testid="money-current-plan" className="text-base font-extrabold text-slate-900 dark:text-white">{planLabel}</p>
            </div>
            {!isPremium && (
              <button
                data-testid="money-upgrade-cta"
                onClick={() => nav("/upgrade")}
                className="h-9 px-4 rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 text-sm font-extrabold"
              >
                Upgrade
              </button>
            )}
          </div>
        </Card>

        {/* Upcoming payments */}
        <Card testid="money-upcoming">
          <SectionHeader icon={<CreditCard className="w-4 h-4 text-indigo-600" />} title="Upcoming payments" />
          {loading && <RowSkeleton />}
          {!loading && upcoming.length === 0 && (
            <p data-testid="money-upcoming-empty" className="text-[12px] text-slate-500 mt-1">Nothing waiting to be paid.</p>
          )}
          {!loading && upcoming.map((b, i) => (
            <PaymentRow key={b.id || i} b={b} testid={`money-upcoming-row-${i}`} tone="indigo" />
          ))}
        </Card>

        {/* Recent payments */}
        <Card testid="money-history">
          <SectionHeader icon={<Receipt className="w-4 h-4 text-emerald-600" />} title="Recent payments" />
          {loading && <RowSkeleton />}
          {!loading && paid.length === 0 && (
            <p data-testid="money-history-empty" className="text-[12px] text-slate-500 mt-1">No payments yet.</p>
          )}
          {!loading && paid.map((b, i) => (
            <PaymentRow key={b.id || i} b={b} testid={`money-history-row-${i}`} tone="emerald" />
          ))}
        </Card>
      </main>
    </div>
  );
}

function Card({ children, testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
    >
      {children}
    </section>
  );
}

function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">{title}</h3>
    </div>
  );
}

function RowSkeleton() {
  return (
    <div className="py-3 flex items-center justify-center">
      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
    </div>
  );
}

function PaymentRow({ b, testid, tone }) {
  const amount = b?.amount_pence != null ? `£${(b.amount_pence / 100).toFixed(2)}` : (b?.amount != null ? `£${Number(b.amount).toFixed(2)}` : "—");
  const dateStr = b?.date || (b?.created_at ? b.created_at.slice(0, 10) : "");
  const time = b?.start_time || b?.time || "";
  const dotCls = tone === "emerald" ? "bg-emerald-400" : "bg-indigo-400";
  return (
    <div data-testid={testid} className="flex items-center gap-3 py-2 border-t border-slate-100 dark:border-slate-800/60 first:border-t-0">
      <span className={`w-2 h-2 rounded-full ${dotCls}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
          Lesson · {dateStr}{time ? ` · ${time}` : ""}
        </p>
        <p className="text-[11px] text-slate-500">{b?.payment_method || "card"}</p>
      </div>
      <p className="text-sm font-extrabold text-slate-900 dark:text-white">{amount}</p>
      <ChevronRight className="w-4 h-4 text-slate-300" />
    </div>
  );
}
